import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Button } from "@/modern/components/ui/button";
import { ChevronDown } from "lucide-react";
import MegaMenuContent from "./MegaMenuContent";

const AppViewButton = () => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false)
  const hoverTimeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);

  // Clear any existing timeout
  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  // Handle mouse enter on trigger button
  const handleTriggerMouseEnter = () => {
    clearHoverTimeout()
    setIsHovering(true)
    setPopoverOpen(true)
  }

  // Handle mouse leave from trigger button
  const handleTriggerMouseLeave = (e) => {
    // If in edit mode, don't close the menu
    if (isEditMode) {
      return
    }

    try {
      // Check if we're moving to the popover content
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && contentRef.current && contentRef.current.contains(relatedTarget)) {
        return // Don't close if moving to content
      }
    } catch (error) {
      // If there's an error checking contains, proceed with closing logic
      console.warn('Error checking relatedTarget contains:', error);
    }

    // Set a small delay to allow moving to content
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isEditMode) {
        // Double-check edit mode before closing
        setIsHovering(false)
        setPopoverOpen(false)
      }
    }, 500)
  }

  // Handle mouse enter on popover content
  const handleContentMouseEnter = () => {
    clearHoverTimeout()
    setIsHovering(true)
  }

  // Handle mouse leave from popover content
  const handleContentMouseLeave = (e) => {
    // If in edit mode, don't close the menu
    if (isEditMode) {
      return
    }

    try {
      // Check if we're moving back to the trigger
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && triggerRef.current && triggerRef.current.contains(relatedTarget)) {
        return // Don't close if moving back to trigger
      }
    } catch (error) {
      // If there's an error checking contains, proceed with closing logic
      console.warn('Error checking relatedTarget contains:', error);
    }

    // Close the popover after a small delay
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isEditMode) {
        // Double-check edit mode before closing
        setIsHovering(false)
        setPopoverOpen(false)
      }
    }, 500)
  }

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      clearHoverTimeout()
    }
  }, [])

  // Handle popover open change (when clicking outside or pressing escape)
  const handleOpenChange = (open) => {
    // If trying to close but in edit mode, prevent closing unless explicitly requested
    if (!open && isEditMode) {
      // Only allow closing if not hovering and not in edit mode
      return
    }

    if (!open && !isHovering) {
      setPopoverOpen(false)
    } else if (open) {
      setPopoverOpen(true)
    }
  }

  // Handle edit mode changes from the menu content
  const handleEditModeChange = (editMode) => {
    setIsEditMode(editMode)

    // If exiting edit mode and not hovering, close the menu
    if (!editMode && !isHovering) {
      // Small delay to allow for smooth transition
      setTimeout(() => {
        if (!isHovering && !editMode) {
          setPopoverOpen(false)
        }
      }, 200)
    }
  }

  const handleMenuItemClick = () => {
    if (!isEditMode) {
      setIsHovering(false)
      setPopoverOpen(false)
    }
  }

  return (
    <Popover open={popoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          className="flex items-center gap-1 !p-0 !bg-transparent transition-all duration-200 border-0 swivu-header-menu-button"
          onMouseEnter={handleTriggerMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
        >
          <span>View</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-screen max-w-6xl border border-border shadow-2xl backdrop-blur-sm bg-background/95 max-h-screen flex flex-col p-0 transition-all duration-200 z-50"
        onMouseEnter={handleContentMouseEnter}
        onMouseLeave={handleContentMouseLeave}
        side="right"
        align="start"
        alignOffset={45}
        sideOffset={-35}
      >
        <MegaMenuContent onMenuItemClick={handleMenuItemClick} onEditModeChange={handleEditModeChange} />
      </PopoverContent>
    </Popover>
  )
}

export default AppViewButton;
